import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Grid,
  Card,
  CardContent,
  Box,
  IconButton,
  Collapse,
  Fab,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  InputAdornment,
  Pagination,
  TextField
} from '@mui/material';
import {
  Email as EmailIcon,
  LocationOn as LocationIcon,
  WhatsApp as WhatsAppIcon,
  Menu as MenuIcon,
  ExpandLess as ExpandLessIcon,
  Home as HomeIcon,
  Info as InfoIcon,
  MedicalServices as MedicalIcon,
  ContactPhone as ContactIcon,
  Instagram as InstagramIcon,
  Storefront as PetshopIcon,
  ImageNotSupported as NoImageIcon,
  Search as SearchIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { collection, getDocs, getFirestore } from 'firebase/firestore';
import app from '../../config/firebase';
import { COLECCION_PETSHOP_PUBLICO, descuentoDe, precioFinal } from '../../services/petshopPublico';

const db = getFirestore(app);

const WHATSAPP = '573216103829';
const PRODUCTOS_POR_PAGINA = 20;
const TARJETA_ANCHO = 260;
const TARJETA_ALTO = 440;

// Para buscar sin distinguir mayúsculas ni tildes
const normalizar = (texto) =>
  (texto || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

const NAV_LINKS = [
  { texto: 'Inicio', ruta: '/', icono: <HomeIcon sx={{ mr: 2 }} /> },
  { texto: 'Servicios', ruta: '/servicios', icono: <MedicalIcon sx={{ mr: 2 }} /> },
  { texto: 'Nosotros', ruta: '/nosotros', icono: <InfoIcon sx={{ mr: 2 }} /> },
  { texto: 'Petshop', ruta: '/petshop', icono: <PetshopIcon sx={{ mr: 2 }} />, activo: true },
  { texto: 'Contacto', ruta: '/contactos', icono: <ContactIcon sx={{ mr: 2 }} /> }
];

const formatoPrecio = (valor) => `$${Number(valor || 0).toLocaleString('es-CO', { maximumFractionDigits: 0 })}`;

const tituloSeccionSx = {
  color: '#602442',
  fontSize: { xs: '1.8rem', sm: '2.2rem', md: '3rem' },
  mb: { xs: 2, sm: 3, md: 4 }
};

const subtituloSeccionSx = {
  color: '#a55e88',
  mb: { xs: 4, sm: 5, md: 6 },
  fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
  px: { xs: 1, sm: 2 }
};

const Petshop = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [productos, setProductos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [categoriaSel, setCategoriaSel] = useState('todas');
  const [pagina, setPagina] = useState(1);
  const inicioProductosRef = useRef(null);
  const [seleccionado, setSeleccionado] = useState(null);

  useEffect(() => {
    const cargarProductos = async () => {
      try {
        const snapshot = await getDocs(collection(db, COLECCION_PETSHOP_PUBLICO));
        const lista = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        // Disponibles primero, luego por nombre
        lista.sort((a, b) => {
          if (a.disponible !== b.disponible) return a.disponible ? -1 : 1;
          return (a.titulo || '').localeCompare(b.titulo || '', 'es');
        });
        setProductos(lista);
      } catch (e) {
        console.error('Error cargando los productos del petshop:', e);
        setError(true);
      } finally {
        setCargando(false);
      }
    };
    cargarProductos();
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    const handleClickOutside = (event) => {
      if (mobileMenuOpen && !event.target.closest('.mobile-menu')) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mobileMenuOpen]);

  const categorias = [...new Set(productos.map((p) => (p.categoria || '').trim()).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b, 'es')
  );
  const hayFiltros = busqueda.trim() !== '' || categoriaSel !== 'todas';

  const productosFiltrados = productos.filter((p) => {
    const coincideCategoria = categoriaSel === 'todas' || (p.categoria || '').trim() === categoriaSel;
    const texto = normalizar(busqueda.trim());
    const coincideTexto =
      !texto ||
      normalizar(p.titulo).includes(texto) ||
      normalizar(p.descripcion).includes(texto) ||
      normalizar(p.categoria).includes(texto);
    return coincideCategoria && coincideTexto;
  });

  const totalPaginas = Math.max(1, Math.ceil(productosFiltrados.length / PRODUCTOS_POR_PAGINA));
  const paginaActual = Math.min(pagina, totalPaginas);
  const productosPagina = productosFiltrados.slice(
    (paginaActual - 1) * PRODUCTOS_POR_PAGINA,
    paginaActual * PRODUCTOS_POR_PAGINA
  );

  const cambiarBusqueda = (valor) => {
    setBusqueda(valor);
    setPagina(1);
  };
  const cambiarCategoria = (valor) => {
    setCategoriaSel(valor);
    setPagina(1);
  };
  const cambiarPagina = (_, valor) => {
    setPagina(valor);
    if (inicioProductosRef.current) {
      inicioProductosRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const linkPedido = (producto) =>
    `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(`Hola, me interesa el producto "${producto.titulo}" del petshop. ¿Está disponible?`)}`;

  return (
    <Box>
      {/* Header/Navbar */}
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: scrolled ? '#fcdcf4' : 'transparent',
          backdropFilter: scrolled ? 'blur(10px)' : 'none',
          boxShadow: scrolled ? '0 2px 10px rgba(0,0,0,0.1)' : 'none',
          zIndex: 1100,
          transition: 'all 0.3s ease'
        }}
      >
        <Toolbar sx={{ py: { xs: 0.5, sm: 1 }, px: { xs: 1, sm: 2 } }}>
          <Box
            sx={{ mr: { xs: 1, sm: 2, md: 4 }, display: 'flex', alignItems: 'center', cursor: 'pointer', flexShrink: 0 }}
            onClick={() => navigate('/')}
          >
            <Box
              component="img"
              src="/logo.png"
              alt="Belkysvet Logo"
              sx={{
                height: { xs: '40px', sm: '50px', md: '60px' },
                width: 'auto',
                maxWidth: { xs: '100px', sm: '200px' },
                mr: { xs: '4px', sm: '8px' }
              }}
            />
            <Typography
              variant="h5"
              sx={{
                color: scrolled ? '#602442' : 'white',
                fontWeight: 'bold',
                fontSize: { xs: '0.8rem', sm: '1rem', md: '1.2rem' },
                display: { xs: 'none', sm: 'block' },
                textShadow: scrolled ? 'none' : '2px 2px 4px rgba(0,0,0,0.8)',
                transition: 'all 0.3s ease'
              }}
            >
              belkysveterinaria
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {/* Menú de navegación para desktop */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: { md: 2, lg: 4 }, mr: 2 }}>
            {NAV_LINKS.map((link) => (
              <Button
                key={link.ruta}
                color="inherit"
                onClick={link.activo ? undefined : () => navigate(link.ruta)}
                sx={{
                  color: scrolled ? '#602442' : 'white',
                  fontWeight: 'bold',
                  fontSize: { md: '1rem', lg: '1.1rem' },
                  backgroundColor: link.activo ? (scrolled ? 'rgba(96, 36, 66, 0.1)' : 'rgba(255, 255, 255, 0.2)') : 'transparent',
                  '&:hover': {
                    backgroundColor: scrolled ? 'rgba(96, 36, 66, 0.1)' : 'rgba(255, 255, 255, 0.2)'
                  },
                  px: { md: 1, lg: 2 },
                  textShadow: scrolled ? 'none' : '1px 1px 2px rgba(0,0,0,0.8)',
                  transition: 'all 0.3s ease'
                }}
              >
                {link.texto}
              </Button>
            ))}
          </Box>

          {/* Botón de menú móvil */}
          <Box sx={{ display: { xs: 'flex', md: 'none' } }} className="mobile-menu">
            <IconButton
              color="inherit"
              aria-label="menu"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              sx={{
                color: scrolled ? '#602442' : 'white',
                '&:hover': { backgroundColor: scrolled ? 'rgba(96, 36, 66, 0.1)' : 'rgba(255, 255, 255, 0.2)' },
                transition: 'all 0.3s ease'
              }}
            >
              {mobileMenuOpen ? <ExpandLessIcon /> : <MenuIcon />}
            </IconButton>
          </Box>
        </Toolbar>

        {/* Menú móvil desplegable */}
        <Collapse in={mobileMenuOpen} className="mobile-menu">
          <Box sx={{ backgroundColor: 'rgba(252, 220, 244, 0.98)', borderTop: '1px solid rgba(96, 36, 66, 0.1)', py: 1 }}>
            {NAV_LINKS.map((link) => (
              <Button
                key={link.ruta}
                fullWidth
                color="inherit"
                onClick={() => {
                  if (!link.activo) navigate(link.ruta);
                  setMobileMenuOpen(false);
                }}
                sx={{
                  color: '#602442',
                  fontWeight: 'bold',
                  justifyContent: 'flex-start',
                  px: 3,
                  py: 1.5,
                  backgroundColor: link.activo ? 'rgba(96, 36, 66, 0.1)' : 'transparent',
                  '&:hover': { backgroundColor: 'rgba(96, 36, 66, 0.1)' }
                }}
              >
                {link.icono}
                {link.texto}
              </Button>
            ))}
          </Box>
        </Collapse>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          backgroundImage: {
            xs: 'linear-gradient(rgba(196, 100, 148, 0.8), rgba(96, 36, 66, 0.8)), url("/petshop.jpg")',
            md: 'linear-gradient(rgba(196, 100, 148, 0.7), rgba(96, 36, 66, 0.7)), url("/petshop.jpg")'
          },
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: { xs: 'scroll', md: 'fixed' },
          color: 'white',
          py: { xs: 6, sm: 8, md: 10 },
          px: { xs: 2, sm: 3 },
          textAlign: 'center',
          minHeight: { xs: '50vh', sm: '60vh', md: '60vh' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pt: { xs: '80px', sm: '90px', md: '100px' }
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            fontWeight="bold"
            sx={{
              textShadow: '3px 3px 6px rgba(0,0,0,0.8)',
              fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3.5rem' },
              lineHeight: { xs: 1.2, sm: 1.3, md: 1.4 },
              mb: { xs: 2, sm: 3, md: 4 }
            }}
          >
            Petshop Belkysveterinaria
          </Typography>
          <Typography
            variant="h5"
            sx={{
              mb: { xs: 3, sm: 4, md: 4 },
              opacity: 0.95,
              textShadow: '2px 2px 4px rgba(0,0,0,0.7)',
              fontSize: { xs: '1rem', sm: '1.3rem', md: '1.5rem' },
              lineHeight: { xs: 1.4, sm: 1.5, md: 1.6 },
              px: { xs: 1, sm: 2 }
            }}
          >
            Todo lo que tu mascota necesita, seleccionado con el mismo cariño con el que la cuidamos
          </Typography>
        </Container>
      </Box>

      {/* Productos */}
      <Box ref={inicioProductosRef} sx={{ backgroundColor: 'white', py: { xs: 4, sm: 6, md: 8 }, px: { xs: 2, sm: 3 }, scrollMarginTop: '70px' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" gutterBottom fontWeight="bold" sx={tituloSeccionSx}>
            Nuestros Productos
          </Typography>
          <Typography variant="h6" align="center" sx={subtituloSeccionSx}>
            Escríbenos por WhatsApp y te lo llevamos a casa (no incluye domicilio).
          </Typography>

          {cargando ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200, gap: 2, color: '#a55e88' }}>
              <CircularProgress size={24} sx={{ color: '#c46494' }} />
              <Typography variant="h6">Cargando productos...</Typography>
            </Box>
          ) : error ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="h6" sx={{ color: '#a55e88' }}>
                No pudimos cargar los productos en este momento. Intenta de nuevo más tarde.
              </Typography>
            </Box>
          ) : productos.length === 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', py: 6 }}>
              <PetshopIcon sx={{ fontSize: '4rem', color: '#c46494', mb: 2 }} />
              <Typography variant="h6" sx={{ color: '#a55e88', mb: 1 }}>
                Pronto tendremos productos disponibles
              </Typography>
              <Typography variant="body2" sx={{ color: '#8a6581', px: 2 }}>
                Estamos preparando nuestro catálogo. ¡Vuelve a visitarnos!
              </Typography>
            </Box>
          ) : (
            <>
              {/* Búsqueda y categorías */}
              <Box sx={{ maxWidth: 600, mx: 'auto', mb: 3 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Buscar producto..."
                  value={busqueda}
                  onChange={(e) => cambiarBusqueda(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#c46494' }} />
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 3, backgroundColor: 'white' }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e3b5cf' },
                    '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#c46494 !important' }
                  }}
                />
              </Box>
              {categorias.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center', mb: 4 }}>
                  {['todas', ...categorias].map((cat) => {
                    const activo = categoriaSel === cat;
                    return (
                      <Chip
                        key={cat}
                        label={cat === 'todas' ? 'Todas' : cat}
                        clickable
                        onClick={() => cambiarCategoria(cat)}
                        variant={activo ? 'filled' : 'outlined'}
                        sx={{
                          fontWeight: 'bold',
                          borderColor: '#c46494',
                          color: activo ? 'white' : '#602442',
                          backgroundColor: activo ? '#c46494' : 'white',
                          '&:hover': { backgroundColor: activo ? '#a55e88' : '#fcdcf4' }
                        }}
                      />
                    );
                  })}
                </Box>
              )}

              {productosFiltrados.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Typography variant="h6" sx={{ color: '#a55e88', mb: 2 }}>
                    No encontramos productos con esa búsqueda
                  </Typography>
                  {hayFiltros && (
                    <Button
                      onClick={() => {
                        setBusqueda('');
                        setCategoriaSel('todas');
                        setPagina(1);
                      }}
                      variant="contained"
                      sx={{
                        backgroundColor: '#c46494',
                        color: 'white',
                        '&:hover': { backgroundColor: '#a55e88' },
                        fontWeight: 'bold',
                        textTransform: 'none',
                        borderRadius: 2,
                        px: 3
                      }}
                    >
                      Limpiar filtros
                    </Button>
                  )}
                </Box>
              ) : (
            <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} justifyContent="center">
              {productosPagina.map((producto) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={producto.id} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Card
                    role="button"
                    tabIndex={0}
                    aria-label={`Ver detalle de ${producto.titulo}`}
                    onClick={() => setSeleccionado(producto)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSeleccionado(producto);
                      }
                    }}
                    sx={{
                      cursor: 'pointer',
                      outlineColor: '#c46494',
                      // Tamaño fijo: todas las tarjetas miden lo mismo sin importar su contenido
                      width: TARJETA_ANCHO,
                      height: TARJETA_ALTO,
                      flexShrink: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'transform 0.3s, box-shadow 0.3s',
                      '&:hover': {
                        transform: { xs: 'none', sm: 'translateY(-5px)' },
                        boxShadow: { xs: 3, sm: 6 }
                      },
                      backgroundColor: producto.disponible ? 'white' : '#f2f2f2',
                      border: producto.disponible ? '1px solid #fcdcf4' : '1px solid #dcdcdc',
                      // Agotado: contenido en grises (el botón y la etiqueta "Agotado" conservan su color)
                    }}
                  >
                    <Box sx={{ position: 'relative', height: 200, flexShrink: 0, backgroundColor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottom: '1px solid #fcdcf4' }}>
                      {producto.imagenURL ? (
                        <Box
                          component="img"
                          src={producto.imagenURL}
                          alt={producto.titulo}
                          loading="lazy"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                          }}
                          sx={{ width: '100%', height: '100%', objectFit: 'contain', p: 1, filter: producto.disponible ? 'none' : 'grayscale(1)' }}
                        />
                      ) : (
                        <NoImageIcon sx={{ fontSize: 56, color: '#d9b8cb' }} />
                      )}
                      {(producto.categoria || '').trim() && (
                        <Chip
                          label={producto.categoria.trim()}
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            cambiarCategoria(producto.categoria.trim());
                          }}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            maxWidth: '55%',
                            fontWeight: 'bold',
                            backgroundColor: producto.disponible ? '#fcdcf4' : '#e0e0e0',
                            color: producto.disponible ? '#602442' : '#757575',
                            '&:hover': { backgroundColor: producto.disponible ? '#f5c6e6' : '#d6d6d6' }
                          }}
                        />
                      )}
                      {!producto.disponible && (
                        <Chip
                          label="Agotado"
                          size="small"
                          sx={{ position: 'absolute', top: 8, right: 8, backgroundColor: '#602442', color: 'white', fontWeight: 'bold' }}
                        />
                      )}
                      {descuentoDe(producto) > 0 && producto.precioVenta > 0 && (
                        <Chip
                          label={`-${descuentoDe(producto)}%`}
                          size="small"
                          sx={{ position: 'absolute', bottom: 8, left: 8, backgroundColor: producto.disponible ? '#e53935' : '#9e9e9e', color: 'white', fontWeight: 'bold' }}
                        />
                      )}
                    </Box>
                    <CardContent sx={{ p: 2, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', '&:last-child': { pb: 2 } }}>
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        title={producto.titulo}
                        sx={{
                          color: producto.disponible ? '#602442' : '#757575',
                          fontSize: '1.1rem',
                          lineHeight: 1.3,
                          height: '2.86rem',
                          mb: 0.5,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          wordBreak: 'break-word'
                        }}
                      >
                        {producto.titulo}
                      </Typography>
                      <Typography
                        variant="body2"
                        title={producto.descripcion}
                        sx={{
                          color: producto.disponible ? '#602442' : '#8a8a8a',
                          lineHeight: 1.5,
                          height: '3.9rem',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          wordBreak: 'break-word'
                        }}
                      >
                        {producto.descripcion}
                      </Typography>
                      <Box sx={{ mt: 'auto' }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 1, mb: 1, minHeight: '2rem' }}>
                          <Typography variant="h5" fontWeight="bold" sx={{ color: producto.disponible ? '#c46494' : '#9e9e9e' }}>
                            {producto.precioVenta > 0 ? formatoPrecio(precioFinal(producto)) : ''}
                          </Typography>
                          {descuentoDe(producto) > 0 && producto.precioVenta > 0 && (
                            <Typography variant="body2" sx={{ color: producto.disponible ? '#9e8a96' : '#b0b0b0', textDecoration: 'line-through' }}>
                              {formatoPrecio(producto.precioVenta)}
                            </Typography>
                          )}
                        </Box>
                        <Button
                          fullWidth
                          variant="contained"
                          startIcon={<WhatsAppIcon />}
                          href={linkPedido(producto)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            backgroundColor: '#602442',
                            '&:hover': { backgroundColor: '#a55e88' },
                            borderRadius: 2,
                            fontWeight: 'bold',
                            textTransform: 'none'
                          }}
                        >
                          {producto.disponible ? 'Pedir por WhatsApp' : 'Consultar'}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
              )}

              {totalPaginas > 1 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: { xs: 4, sm: 5 }, gap: 1 }}>
                  <Pagination
                    count={totalPaginas}
                    page={paginaActual}
                    onChange={cambiarPagina}
                    siblingCount={0}
                    sx={{
                      '& .MuiPaginationItem-root': { color: '#602442', fontWeight: 'bold' },
                      '& .MuiPaginationItem-root.Mui-selected': { backgroundColor: '#c46494', color: 'white' },
                      '& .MuiPaginationItem-root.Mui-selected:hover': { backgroundColor: '#a55e88' }
                    }}
                  />
                  <Typography variant="body2" sx={{ color: '#a55e88' }}>
                    Mostrando {(paginaActual - 1) * PRODUCTOS_POR_PAGINA + 1}–
                    {Math.min(paginaActual * PRODUCTOS_POR_PAGINA, productosFiltrados.length)} de {productosFiltrados.length} productos
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Container>
      </Box>

      {/* Detalle del producto */}
      <Dialog
        open={Boolean(seleccionado)}
        onClose={() => setSeleccionado(null)}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            m: { xs: 1.5, sm: 3 },
            maxHeight: { xs: 'calc(100% - 24px)', sm: 'calc(100% - 48px)' }
          }
        }}
      >
        {seleccionado && (
          <DialogContent
            sx={{
              p: 0,
              position: 'relative',
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              overflow: 'auto'
            }}
          >
            <IconButton
              aria-label="Cerrar"
              onClick={() => setSeleccionado(null)}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 1,
                backgroundColor: 'rgba(255,255,255,0.9)',
                color: '#602442',
                '&:hover': { backgroundColor: '#fcdcf4' }
              }}
            >
              <CloseIcon />
            </IconButton>

            {/* Foto: a la izquierda en pantallas anchas, arriba en celular */}
            <Box
              sx={{
                backgroundColor: '#fff',
                borderBottom: { xs: '1px solid #fcdcf4', sm: 'none' },
                borderRight: { xs: 'none', sm: '1px solid #fcdcf4' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: { xs: '0 0 auto', sm: '0 0 45%' },
                height: { xs: '30vh', sm: 'auto' },
                minHeight: { xs: 140, sm: 320 },
                p: 1
              }}
            >
              {seleccionado.imagenURL ? (
                <Box
                  component="img"
                  src={seleccionado.imagenURL}
                  alt={seleccionado.titulo}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                  }}
                  sx={{
                    width: '100%',
                    height: '100%',
                    maxHeight: { xs: '100%', sm: '70vh' },
                    objectFit: 'contain',
                    filter: seleccionado.disponible ? 'none' : 'grayscale(1)'
                  }}
                />
              ) : (
                <NoImageIcon sx={{ fontSize: 72, color: '#d9b8cb' }} />
              )}
            </Box>

            {/* Información */}
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                p: { xs: 2, sm: 3.5 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}
            >
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5, pr: { sm: 5 } }}>
                {(seleccionado.categoria || '').trim() && (
                  <Chip
                    label={seleccionado.categoria.trim()}
                    size="small"
                    sx={{ fontWeight: 'bold', backgroundColor: '#fcdcf4', color: '#602442' }}
                  />
                )}
                {descuentoDe(seleccionado) > 0 && seleccionado.precioVenta > 0 && (
                  <Chip
                    label={`-${descuentoDe(seleccionado)}%`}
                    size="small"
                    sx={{ fontWeight: 'bold', backgroundColor: '#e53935', color: 'white' }}
                  />
                )}
                <Chip
                  label={seleccionado.disponible ? 'Disponible' : 'Agotado'}
                  size="small"
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: seleccionado.disponible ? '#e8f5e9' : '#602442',
                    color: seleccionado.disponible ? '#2e7d32' : 'white'
                  }}
                />
              </Box>
              <Typography
                variant="h5"
                fontWeight="bold"
                sx={{ color: '#602442', mb: 1, wordBreak: 'break-word', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
              >
                {seleccionado.titulo}
              </Typography>
              {seleccionado.descripcion && (
                <Typography
                  variant="body1"
                  sx={{
                    color: '#602442',
                    lineHeight: 1.6,
                    mb: 2,
                    whiteSpace: 'pre-line',
                    wordBreak: 'break-word',
                    fontSize: { xs: '0.95rem', sm: '1rem' }
                  }}
                >
                  {seleccionado.descripcion}
                </Typography>
              )}
              {seleccionado.precioVenta > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
                  <Typography
                    variant="h4"
                    fontWeight="bold"
                    sx={{ color: '#c46494', fontSize: { xs: '1.6rem', sm: '2rem' } }}
                  >
                    {formatoPrecio(precioFinal(seleccionado))}
                  </Typography>
                  {descuentoDe(seleccionado) > 0 && (
                    <Typography variant="h6" sx={{ color: '#9e8a96', textDecoration: 'line-through' }}>
                      {formatoPrecio(seleccionado.precioVenta)}
                    </Typography>
                  )}
                </Box>
              )}
              <Button
                fullWidth
                variant="contained"
                startIcon={<WhatsAppIcon />}
                href={linkPedido(seleccionado)}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  backgroundColor: '#602442',
                  '&:hover': { backgroundColor: '#a55e88' },
                  borderRadius: 2,
                  fontWeight: 'bold',
                  textTransform: 'none',
                  py: 1.1
                }}
              >
                {seleccionado.disponible ? 'Pedir por WhatsApp' : 'Consultar por WhatsApp'}
              </Button>
            </Box>
          </DialogContent>
        )}
      </Dialog>

      {/* Footer simplificado */}
      <Box sx={{ backgroundColor: '#602442', color: 'white', py: { xs: 3, sm: 4 }, px: { xs: 2, sm: 3 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' }, textAlign: { xs: 'center', sm: 'left' } }}>
                <Box
                  component="img"
                  src="/logo192.png"
                  alt="Belkysvet Logo"
                  sx={{ height: { xs: '60px', sm: '80px' }, width: 'auto', mr: { xs: 0, sm: '20px' }, mb: { xs: '10px', sm: 0 } }}
                />
                <Box>
                  <Typography variant="h6" sx={{ color: '#c46494', mb: 1, fontWeight: 'bold', fontSize: { xs: '1.2rem', sm: '1.25rem' } }}>
                    Belkysveterinaria
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.4, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                    Atención veterinaria profesional en la comodidad de tu hogar
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                    <LocationIcon sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, color: '#c46494', mr: 0.5 }} />
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' }, color: '#fcdcf4', fontWeight: '500' }}>
                      Servicio a domicilio - Popayán, Cauca
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', md: 'flex-end' }, textAlign: { xs: 'center', md: 'right' }, gap: 1 }}>
                {[
                  { href: `https://wa.me/${WHATSAPP}?text=Hola,%20me%20gustaría%20agendar%20una%20cita`, icono: <WhatsAppIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.3rem' } }} />, texto: '@belkys.veterinaria', externo: true },
                  { href: 'mailto:belkys.veterinaria@gmail.com', icono: <EmailIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.3rem' } }} />, texto: 'belkys.veterinaria@gmail.com' },
                  { href: 'https://www.instagram.com/belkys.veterinaria/', icono: <InstagramIcon sx={{ fontSize: { xs: '1.5rem', sm: '1.8rem' } }} />, texto: '@belkys.veterinaria', externo: true }
                ].map((contacto) => (
                  <Box key={contacto.href} sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'center', md: 'flex-end' } }}>
                    <IconButton
                      component="a"
                      href={contacto.href}
                      target={contacto.externo ? '_blank' : undefined}
                      rel={contacto.externo ? 'noopener noreferrer' : undefined}
                      sx={{ color: '#c46494', '&:hover': { color: '#fcdcf4', transform: 'scale(1.1)' }, transition: 'all 0.3s ease', p: 1 }}
                    >
                      {contacto.icono}
                    </IconButton>
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, fontWeight: '500', ml: 1 }}>
                      {contacto.texto}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ textAlign: 'center', mt: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 3 }, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
            <Typography variant="body2" sx={{ opacity: 0.8, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
              © 2025 Belkysveterinaria. Todos los derechos reservados.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Botón flotante de WhatsApp */}
      <Fab
        color="success"
        aria-label="whatsapp"
        sx={{
          position: 'fixed',
          bottom: { xs: 16, sm: 20 },
          right: { xs: 16, sm: 20 },
          backgroundColor: '#c46494',
          '&:hover': { backgroundColor: '#a55e88' },
          width: { xs: 48, sm: 56 },
          height: { xs: 48, sm: 56 },
          zIndex: 1000
        }}
        href={`https://wa.me/${WHATSAPP}?text=Hola,%20me%20gustaría%20agendar%20una%20cita`}
        target="_blank"
      >
        <WhatsAppIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
      </Fab>
    </Box>
  );
};

export default Petshop;
